/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
/* eslint-disable max-lines-per-function */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { ClickOutside } from '../ClickOutside';

import './input.scss';
import './combobox.scss';
import './icon.scss';
import './list.scss';

const MAX_LIST_ITEMS_HEIGHT = 220;
const LOADING = 'Загрузка...';
const NOT_FOUND = 'NOT_FOUND';
const ARROW_DOWN_KEYCODE = 40;
const ARROW_UP_KEYCODE = 38;
const ENTER_KEYCODE = 13;
const notFoundItem = [
    {
        id: NOT_FOUND,
        name: 'Нет совпадений',
    },
];

const input = require('b_')
    .with('input');
const combobox = require('b_')
    .with('combobox');
const list = require('b_')
    .with('list');
const icon = require('b_')
    .with('icon');

const formatPrice = (price) => {
    const normalizedPrice = Number(price);

    return Number.isFinite(normalizedPrice)
        ? normalizedPrice.toFixed(2).replace('.', ',')
        : price;
};

// eslint-disable-next-line max-len
const getVisibleItemsWithSelectedItem = (withVisibilityItems, arr, selectedValue) => (!withVisibilityItems ? arr : arr.filter(({
    id,
    visible,
}) => id === selectedValue || visible));

export const Selector = ({
    metaData,
    value,
    error,
    disabled,
    placeholder,
    name,
    isLoading,
    onChange,
    id,
    mod,
    typeInput = 'text',
    withoutDefaultSelect,
    defaultSelectItem = {
        id: 0,
        name: '-- не установлено --',
    },
    isAvvaPolicy,
    withVisibilityItems,
    readOnly = false,
    withClear = true,
    padding,
    onClear,
    pre,
}) => {
    const [ itemsIsVisible, changeVisibility ] = useState(false);
    const [ displayName, setDisplayName ] = useState('');
    const [ viewListItems, setViewListItems ] = useState(null);

    const setBaseListInitItems = useCallback(() => {
        const items = getVisibleItemsWithSelectedItem(withVisibilityItems, metaData, value);
        const arrItems = withoutDefaultSelect ? items : [ defaultSelectItem, ...items ];
        setViewListItems(arrItems);
    }, [ withVisibilityItems, withoutDefaultSelect, metaData, value, defaultSelectItem ]);

    const upward = useMemo(() => {
        // eslint-disable-next-line no-undef
        const element = document.getElementById(id);

        if (!element || !itemsIsVisible) {
            return;
        }

        const selector = element.getBoundingClientRect();
        const spaceAtTheBottom =
            // eslint-disable-next-line no-undef
            document.documentElement.clientHeight - selector.top - selector.height - MAX_LIST_ITEMS_HEIGHT;
        const spaceAtTheTop = selector.top - MAX_LIST_ITEMS_HEIGHT;

        // eslint-disable-next-line consistent-return
        return spaceAtTheBottom < 0 && spaceAtTheTop > spaceAtTheBottom;
    }, [ id, itemsIsVisible ]);

    useEffect(() => {
        if (!value && typeInput !== 'button') {
            setDisplayName('');
        }
        if (metaData.length) {
            setBaseListInitItems();
        }
    }, [ value, itemsIsVisible, typeInput, metaData, setBaseListInitItems ]);

    useEffect(() => {
        if (metaData.length) {
            if (value) {
                const item = metaData.find((x) => {
                    if (isAvvaPolicy) {
                        return x.cards && x.cards.some(valueId => `${valueId}` === `${value}`);
                    }
                    return `${x.id}` === `${value}`;
                });
                setDisplayName(item && item.name);
            } else if (typeInput === 'button') {
                const item = metaData.find(({ id: valueId }) => `${valueId}` === `${value}`);
                setDisplayName(item && item.name);
            } else {
                setDisplayName('');
            }
            setBaseListInitItems();
        }
    }, [ value, metaData, typeInput, setBaseListInitItems, isAvvaPolicy ]);

    useEffect(() => {
        const hasHoveringItem = viewListItems && viewListItems.findIndex(item => item.hovering) > -1;

        if (hasHoveringItem && hasHoveringItem !== -1) {
            // eslint-disable-next-line no-undef
            const element = window.document.querySelector('.list__row_hovering');
            element.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
                inline: 'center',
            });
        }
    }, [viewListItems]);

    const onSearchHandler = ({ target: { value: targetValue } }) => {
        if (!itemsIsVisible) {
            changeVisibility(true);
        }
        if (value) {
            onChange();
        }

        const items = getVisibleItemsWithSelectedItem(withVisibilityItems, metaData, value);
        const searchItems = items.filter(({ name: nameItem }) => nameItem.toLowerCase()
            .includes(targetValue.toLowerCase()));
        setViewListItems(searchItems.length === 0 ? notFoundItem : searchItems);

        setDisplayName(targetValue);
    };

    const listenerKeyDown = ({ keyCode }) => {
        const indexHoveringItem = viewListItems.findIndex(({ hovering }) => hovering);

        if (keyCode === ARROW_UP_KEYCODE) {
            const newHoveringIndex =
                indexHoveringItem > -1 && indexHoveringItem - 1 >= 0
                    ? viewListItems.findIndex((item, index) => index === indexHoveringItem - 1)
                    : viewListItems.length - 1;
            setViewListItems(
                viewListItems.map((el, index) => ({
                    ...el,
                    hovering: index === newHoveringIndex,
                })),
            );
        }

        if (keyCode === ARROW_DOWN_KEYCODE) {
            const newHoveringIndex =
                indexHoveringItem > -1 && indexHoveringItem + 1 <= viewListItems.length - 1
                    ? viewListItems.findIndex((item, index) => index === indexHoveringItem + 1)
                    : 0;

            setViewListItems(
                viewListItems.map((el, index) => ({
                    ...el,
                    hovering: index === newHoveringIndex,
                })),
            );
        }

        if (keyCode === ENTER_KEYCODE && indexHoveringItem !== -1) {
            const { id: targetId } = viewListItems.find(({ hovering }) => hovering);
            onChange(targetId);
            changeVisibility(false);
        }
    };

    return (
        <ClickOutside onClick={() => changeVisibility(false)} open={itemsIsVisible}>
            <input
                id={id}
                className={`${input({
                    large: true,
                    error: !!error,
                    selector: true,
                    ...mod,
                })} `}
                style={
                    padding
                        ? {
                            paddingLeft: padding,
                            paddingRight: padding,
                        }
                        : {}
                }
                type={typeInput}
                name={name}
                disabled={disabled}
                placeholder={isLoading ? LOADING : placeholder}
                onClick={() => changeVisibility(!itemsIsVisible)}
                onChange={onSearchHandler}
                onKeyDown={listenerKeyDown}
                value={displayName}
                autoComplete="off"
                spellCheck={false}
                readOnly={readOnly}
            />
            <span
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                }}>
                {withClear && value && typeof onClear === 'function' ? (
                    <i
                        onClick={() => !disabled && onClear()}
                        className={`icon_close--outline ${icon({ clear: true })}`}
                        style={{ marginRight: 10 }}
                    />
                ) : null}
                <i
                    onClick={() => !disabled && changeVisibility(!itemsIsVisible)}
                    className={`icon_caret--down ${icon({
                        reversed: itemsIsVisible,
                        disabled,
                    })}`}
                />
            </span>
            <ul
                className={`${combobox('list-items', {
                    visible: metaData && itemsIsVisible && metaData.length > 0,
                    upward,
                })} ${list('container')}`}>
                {viewListItems &&
                    viewListItems.map(({
                        id: idItem,
                        name: nameItem,
                        hovering,
                        red,
                        disabled: disabledItem,
                        price,
                    }) => (
                        <li
                            key={`${id}_list-item_${idItem}`}
                            role="presentation"
                            className={`${combobox('list-item', { visible: itemsIsVisible })} ${combobox('list-item', { pre })} ${list('row', {
                                selected: `${value}` === `${idItem}`,
                                hovering,
                                'not-found': idItem === NOT_FOUND,
                            })} ${combobox('list-item', { red })}`}
                            onClick={() => {
                                if (disabledItem) {
                                    return;
                                }
                                onChange(idItem);
                                changeVisibility(false);
                            }}>
                            {nameItem}
                            {price != null && (
                                <div style={{ marginTop: 4, fontWeight: 600 }}>
                                    {formatPrice(price)} <span className="nbrb-icon currency-symbol">BYN</span>
                                </div>
                            )}
                        </li>
                    ))}
            </ul>
        </ClickOutside>
    );
};

Selector.propTypes = {
    metaData: PropTypes.instanceOf(Array).isRequired,
    onChange: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    defaultSelectItem: PropTypes.instanceOf(Object),
    mod: PropTypes.instanceOf(Object),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    value: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
    error: PropTypes.string,
    name: PropTypes.string,
    isLoading: PropTypes.bool,
    typeInput: PropTypes.string,
    withoutDefaultSelect: PropTypes.bool,
    isAvvaPolicy: PropTypes.bool,
    withVisibilityItems: PropTypes.bool,
    readOnly: PropTypes.bool,
};

Selector.defaultProps = {
    typeInput: 'text',
    defaultSelectItem: {
        id: 0,
        name: '-- не установлено --',
    },
    withVisibilityItems: false,
    withoutDefaultSelect: true,
    isAvvaPolicy: false,
    isLoading: false,
    readOnly: false,
    disabled: false,
    error: null,
    value: null,
    mod: null,
    name: '',
    placeholder: '',
};
